import { FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  AuthorizedButton,
  Container,
  DataTable,
  OrganizationModal,
  PageTitle,
  SExportButton,
} from '../../../components';
import { INITIAL_VALUES } from './const';
import styles from './index.module.scss';
import UseHttpService from '../../../services/hook';
import { useAppDispatch, useAppSelector } from '../../../store';
import validationSchema from './validations';
import { getColumns } from './columns';
import { tableActions } from '../../../store/reducers/tableReducer';
import {
  IOrganizationSettingsDataSource,
  IOrganizationSettingsInitialValues,
} from '../../../types';
import { modalTypes } from '../../../assets/enums';
import { downloadFile } from '../../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../../utils/functions';
import useFetchParameters from '../../../hooks';
import OrganizationSettingsService from '../../../api-clients/OrganizationSettingsService';

const OrganizationSettings: FC = () => {
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);
  const dispatch = useAppDispatch();
  const executeService = UseHttpService();

  // State
  const [selectedItem, setSelectedItem] = useState<
    IOrganizationSettingsDataSource | undefined
  >(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<
    IOrganizationSettingsDataSource[]
  >([]);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
  });
  const organizationSettingsService = useMemo(
    () => new OrganizationSettingsService(executeService, parameters),
    [executeService, parameters],
  );

  // Handlers
  const handleFetchData = async () => {
    const response = await organizationSettingsService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      setDataSource(response.results);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem = await organizationSettingsService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleClickDelete = async (id: number) => {
    try {
      await organizationSettingsService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IOrganizationSettingsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    const response = await organizationSettingsService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: IOrganizationSettingsInitialValues) => {
    try {
      await organizationSettingsService.submitForm(values, selectedItem);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IOrganizationSettingsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  // Columns
  const columns = getColumns(handleClickDelete, handleOpenEditModal);

  // Form Management
  const formik = useFormik<IOrganizationSettingsInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IOrganizationSettingsDataSource,
        IOrganizationSettingsInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Şirket Ayarları" />
      <Flex vertical gap="middle">
        <Flex justify="flex-end" gap={5}>
          <SExportButton onClick={handleClickExport} />
          <AuthorizedButton
            model="organization"
            action="add"
            className={styles.button}
            onClick={handleOpenModal}
          >
            <PlusOutlined className={styles.buttonIcon} />
            <div className={styles.buttonText}>Yeni Şirket Ekle</div>
          </AuthorizedButton>
        </Flex>
        <DataTable<IOrganizationSettingsDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
        />
      </Flex>
      <OrganizationModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Şirket Tanımlama"
        subTitle="Yeni Şirket Tanımlama Formu"
        formik={formik}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
      />
      <OrganizationModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Şirket Güncelleme"
        subTitle="Şirket Güncelleme"
        formik={formik}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
      />
    </Container>
  );
};

export default OrganizationSettings;
